/**
 * React Query hooks for one-tap Add to outreach (mb-frontend#4638).
 *
 * The preview is fetched when a confirmation opens; an enrollment is polled while it is
 * still moving (every 5 s) and left alone once it has settled. Mutations invalidate the
 * same lists the old promote path did, so every surface that shows the row catches up.
 */

import {
  useMutation, useQuery, useQueryClient, type QueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  cancelEnrollment,
  createEnrollment,
  enrollmentTargetKey,
  getEnrollment,
  getEnrollmentPreview,
  getSenderReadiness,
  retryEnrollment,
  type EnrollmentResponse,
  type EnrollmentTarget,
} from '@/services/signalEnrollments';
import { getOutreachTemplateRoster, rosterSourceKey, type RosterSource } from '@/services/outreachTemplates';
import { isActiveEnrollmentStatus } from '@/lib/outreach/signalEnrollmentContract';
import { ASSIGNMENT_QUERY_KEYS } from './useAssignOwner';

export const ENROLLMENT_QUERY_KEYS = {
  preview: (target: EnrollmentTarget) => ['signal-enrollment', 'preview', enrollmentTargetKey(target)] as const,
  enrollment: (enrollmentId: string) => ['signal-enrollment', enrollmentId] as const,
  readiness: ['signal-enrollment', 'readiness'] as const,
  templateRoster: (sourceKey: string) => ['signal-enrollment', 'template-roster', sourceKey] as const,
};

/** How often an in-flight intent is re-read. Discovery polls at 60 s, the executor at 60 s. */
export const ENROLLMENT_POLL_MS = 5_000;

/** Every list that shows an Outreach state; the promote path invalidated the same set. */
export function invalidateOutreachLists(queryClient: QueryClient): void {
  // Enrolling also makes the rep the account's owner (mb-server: unowned signals follow), so
  // everything that shows an owner refreshes too — the open panel included.
  [...ASSIGNMENT_QUERY_KEYS, ['signals']].forEach((queryKey) => { void queryClient.invalidateQueries({ queryKey }); });
}

/**
 * With a `templateId`, the preview describes that template; switching templates keeps the
 * previous answer on screen until the new one lands, so the confirm never blanks while a rep
 * browses the dropdown. Without one the key is exactly `preview(target)`, the entry the
 * recheck in useAddToOutreach.open() fetches into.
 */
export const useEnrollmentPreview = (target: EnrollmentTarget | null, enabled = true, templateId: number | null = null) => useQuery({
  queryKey: target
    ? (templateId === null ? ENROLLMENT_QUERY_KEYS.preview(target) : [...ENROLLMENT_QUERY_KEYS.preview(target), templateId])
    : ['signal-enrollment', 'preview', 'none'],
  queryFn: () => getEnrollmentPreview(target as EnrollmentTarget, templateId),
  // Only ever the same target's previous answer: another account's preview must never stand in.
  placeholderData: (previous, previousQuery) => (
    target && previousQuery?.queryKey[2] === enrollmentTargetKey(target) ? previous : undefined
  ),
  enabled: Boolean(target) && enabled,
  staleTime: 15_000,
});

/**
 * mb-frontend#4638 phase 2: the confirm line's template dropdown — for a signal, or for an
 * account whose activity has no signal row yet (the type and company Enroll will create it
 * with, from the preview), so the picker never depends on a signal having been evaluated.
 */
export const useOutreachTemplateRoster = (source: RosterSource | null, enabled = true) => useQuery({
  queryKey: source ? ENROLLMENT_QUERY_KEYS.templateRoster(rosterSourceKey(source)) : ['signal-enrollment', 'template-roster', 'none'],
  queryFn: () => getOutreachTemplateRoster(source as RosterSource),
  enabled: Boolean(source) && enabled,
  staleTime: 15_000,
});

export const useSenderReadiness = (enabled = true) => useQuery({
  queryKey: ENROLLMENT_QUERY_KEYS.readiness,
  queryFn: getSenderReadiness,
  enabled,
  staleTime: 30_000,
});

export const useEnrollment = (enrollmentId: string | null, initialData?: EnrollmentResponse) => useQuery({
  queryKey: enrollmentId ? ENROLLMENT_QUERY_KEYS.enrollment(enrollmentId) : ['signal-enrollment', 'none'],
  queryFn: () => getEnrollment(enrollmentId as string),
  enabled: Boolean(enrollmentId),
  initialData,
  refetchInterval: (query) => {
    const data = query.state.data;
    return data && isActiveEnrollmentStatus(data.status) ? ENROLLMENT_POLL_MS : false;
  },
});

/** Seed the enrollment cache from a response we already hold, so the panel does not refetch. */
export const useSeedEnrollment = () => {
  const queryClient = useQueryClient();
  return useCallback((enrollment: EnrollmentResponse) => {
    queryClient.setQueryData(ENROLLMENT_QUERY_KEYS.enrollment(enrollment.id), enrollment);
  }, [queryClient]);
};

export const useCreateEnrollment = () => {
  const queryClient = useQueryClient();
  const seed = useSeedEnrollment();
  return useMutation({
    // Wrapped, not passed bare: react-query calls mutationFn(variables, context), which would
    // land its context object in the client's optional `surface` argument.
    mutationFn: (body: Parameters<typeof createEnrollment>[0]) => createEnrollment(body),
    onSuccess: (enrollment) => {
      seed(enrollment);
      invalidateOutreachLists(queryClient);
    },
  });
};

export const useCancelEnrollment = () => {
  const queryClient = useQueryClient();
  const seed = useSeedEnrollment();
  return useMutation({
    mutationFn: (enrollmentId: string) => cancelEnrollment(enrollmentId),
    onSuccess: (enrollment) => {
      seed(enrollment);
      invalidateOutreachLists(queryClient);
    },
  });
};

export const useRetryEnrollment = () => {
  const queryClient = useQueryClient();
  const seed = useSeedEnrollment();
  return useMutation({
    mutationFn: (enrollmentId: string) => retryEnrollment(enrollmentId),
    onSuccess: (enrollment) => {
      seed(enrollment);
      invalidateOutreachLists(queryClient);
    },
  });
};
